// ABOUTME: The single Customize surface — add links, install widgets, set appearance.
// ABOUTME: Its open tab lives in the dashboard store, so each opener chooses the tab it lands on.

import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useDashboardStore, SHEET_TABS } from "@/stores/dashboard";
import AddLinkCard from "./AddLinkCard";
import WidgetGallery from "./WidgetGallery";
import AppearanceTab from "./customize/AppearanceTab";
import SuggestedLinksTab from "./customize/SuggestedLinksTab";

interface AddDashboardSheetProps {
  onAddLink: (link: {
    url: string;
    name?: string;
    col?: number;
    tags?: string[];
    bgColor?: string;
  }) => void;
  loading?: boolean;
  onWidgetInstall: (name: string) => void;
  onWidgetRemove: (name: string) => void;
  installedWidgets: { [key: string]: boolean };
  /** URLs already on the dashboard, so Suggested can mark them as added. */
  existingUrls?: string[];
  columnCount: number;
  onChangeColumns: (count: number) => void;
}

export function AddDashboardSheet({
  onAddLink,
  loading = false,
  onWidgetInstall,
  onWidgetRemove,
  installedWidgets,
  existingUrls,
  columnCount,
  onChangeColumns,
}: AddDashboardSheetProps) {
  const { sheetTab: tab, openSheet, closeSheet, setJustAddedWidget } = useDashboardStore();

  const safeColumnCount = typeof columnCount === "number" && columnCount >= 2 ? columnCount : 3;

  const handleWidgetInstall = (name: string) => {
    onWidgetInstall(name);
    // Signal the dashboard, a sibling, to shimmer the new widget.
    setJustAddedWidget(name);
    setTimeout(() => {
      if (useDashboardStore.getState().justAddedWidget === name) {
        setJustAddedWidget(null);
      }
    }, 1200);
  };

  return (
    <Sheet
      open={tab !== null}
      onOpenChange={(open) => {
        if (!open) closeSheet();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-heading text-lg font-semibold">Customize</SheetTitle>
        </SheetHeader>

        <div
          className="flex flex-row gap-5 mt-4 border-b border-[var(--color-border)]"
          role="tablist"
        >
          {SHEET_TABS.map((tabName) => (
            <button
              key={tabName}
              type="button"
              role="tab"
              aria-selected={tab === tabName}
              className={`-mb-px px-1 py-2 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-secondary)] rounded-sm ${
                tab === tabName
                  ? "border-[var(--color-accent)] text-[var(--color-accent)]"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
              }`}
              onClick={() => openSheet(tabName)}
            >
              {tabName}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-6">
          {tab === "Links" && (
            <>
              <AddLinkCard
                onAdd={onAddLink}
                loading={loading}
                onDismiss={() => {}} // Stay open so multiple links can be added
                hideCancel={true}
              />
              <SuggestedLinksTab onAddLink={onAddLink} existingUrls={existingUrls} />
            </>
          )}

          {tab === "Widgets" && (
            <WidgetGallery
              installed={installedWidgets}
              onInstall={handleWidgetInstall}
              onRemove={onWidgetRemove}
            />
          )}

          {tab === "Appearance" && (
            <AppearanceTab columnCount={safeColumnCount} onChangeColumns={onChangeColumns} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default AddDashboardSheet;
